<template>
  <component
    :is="tooltipWrapper"
    v-bind="tooltipProps"
    class="change-pay-card-small-tooltip"
  >
    <div
      class="change-pay-card-small"
      :class="rootClasses"
    >
      <div
        v-if="showStatusRail"
        class="change-pay-card-small__rail"
        :class="`change-pay-card-small__rail--${statusRailColor}`"
        aria-hidden="true"
      />

      <div v-if="resolvedShow === 'Buttons'" class="change-pay-card-small__buttons-body">
        <template v-if="resolvedStatus === 'ProposeChanges'">
          <ChangePayCardSmallButtons
            button="evaluate-eligibility"
            @click="handleEvaluateEligibility"
          />
          <NotAllowedReason
            v-if="blocked"
            :title="blocked.title"
            :description="blocked.description"
            :target-id="blocked.versionId"
            @open="emit('blocked-open', blocked)"
          />
          <EXTooltip
            v-else-if="proposeChangesBlocked"
            placement="top"
            title="Propose Changes"
            description="Edit, delete, or void the open proposal first"
            :delay="200"
          >
            <ChangePayCardSmallButtons
              button="propose-changes"
              :disabled="true"
            />
          </EXTooltip>
          <ChangePayCardSmallButtons
            v-else
            button="propose-changes"
            @click="handleProposeChanges"
          />
          <p v-if="nextEvalDate" class="change-pay-card-small__next-eval">
            Next Eval: {{ nextEvalDate }}
          </p>
        </template>
        <NotAllowedReason
          v-else-if="blocked"
          :title="blocked.title"
          :description="blocked.description"
          :target-id="blocked.versionId"
          @open="emit('blocked-open', blocked)"
        />
        <ChangePayCardSmallButtons
          v-else-if="resolvedStatus === 'ProposeFirstPlan'"
          button="propose-first-pay-plan"
          @click="handleProposeFirstPlan"
        />
        <ChangePayCardSmallButtons
          v-else-if="resolvedStatus === 'ProposePayPlan'"
          button="propose-pay-plan"
          :label="buttonLabel"
          @click="handleProposePayPlan"
        />
      </div>

      <div
        v-else-if="resolvedShow === 'FuturePlan'"
        class="change-pay-card-small__future-plan"
        role="button"
        tabindex="0"
        @click="handleCardClick"
        @keydown.enter="handleCardClick"
        @keydown.space.prevent="handleCardClick"
      >
        <div class="change-pay-card-small__future-plan-top">
          <span class="change-pay-card-small__amount">{{ displayAmount }}</span>
          <span class="change-pay-card-small__basis">{{ displayBasis }}</span>
          <span class="change-pay-card-small__spacer" aria-hidden="true" />
          <div v-if="showMoreActions" class="change-pay-card-small__more">
            <MenuIcon :dropdownItems="menuItems" :width="200" @onClick="handleMenuAction">
              <template #activator>
                <button
                  type="button"
                  class="change-pay-card-small__more-button"
                  aria-label="More actions"
                  aria-haspopup="menu"
                >
                  <img :src="dotsVerticalIcon" alt="" width="16" height="16" />
                </button>
              </template>
            </MenuIcon>
          </div>
          <button
            v-if="showCloseButton"
            type="button"
            class="change-pay-card-small__close"
            aria-label="Remove upcoming pay plan"
            @click.stop="handleRemove"
          >
            <img :src="xCloseIcon" alt="" />
          </button>
        </div>
        <p class="change-pay-card-small__future-plan-label">{{ futurePlanLabel }}</p>
      </div>

      <div
        v-else
        class="change-pay-card-small__body"
        role="button"
        tabindex="0"
        @click="handleCardClick"
        @keydown.enter="handleCardClick"
        @keydown.space.prevent="handleCardClick"
      >
        <div class="change-pay-card-small__header-row">
          <span class="change-pay-card-small__header-icon" aria-hidden="true">
            <img
              v-if="resolvedShow === 'Evaluation'"
              :src="activityHeartIcon"
              alt=""
              class="change-pay-card-small__header-icon-img"
            />
            <span v-else class="change-pay-card-small__settings-icon">
              <span class="change-pay-card-small__settings-accent">
                <img :src="settingsAccentIcon" alt="" />
              </span>
              <span class="change-pay-card-small__settings-glyph">
                <img :src="settingsGlyphIcon" alt="" />
              </span>
            </span>
          </span>
          <span class="change-pay-card-small__date">{{ displayDate }}</span>
          <div v-if="showMoreActions" class="change-pay-card-small__more">
            <MenuIcon :dropdownItems="menuItems" :width="200" @onClick="handleMenuAction">
              <template #activator>
                <button
                  type="button"
                  class="change-pay-card-small__more-button"
                  aria-label="More actions"
                  aria-haspopup="menu"
                >
                  <img :src="dotsVerticalIcon" alt="" width="16" height="16" />
                </button>
              </template>
            </MenuIcon>
          </div>
          <button
            v-if="showCloseButton"
            type="button"
            class="change-pay-card-small__close"
            :aria-label="resolvedShow === 'Evaluation' ? 'Remove evaluation' : 'Remove proposal'"
            @click.stop="handleRemove"
          >
            <img :src="xCloseIcon" alt="" />
          </button>
        </div>

        <div v-if="resolvedShow === 'Proposal'" class="change-pay-card-small__summary-row">
          <span class="change-pay-card-small__summary">{{ displayAmount }}</span>
        </div>

        <div v-else-if="resolvedShow === 'Evaluation'" class="change-pay-card-small__badge-row">
          <span
            class="change-pay-card-small__badge"
            :class="`change-pay-card-small__badge--${resolvedStatus === 'Eligible' ? 'eligible' : 'not-eligible'}`"
          >
            <span class="change-pay-card-small__badge-dot" aria-hidden="true" />
            {{ resolvedStatus === 'Eligible' ? 'Eligible' : 'Not Eligible' }}
          </span>
        </div>
      </div>
    </div>
  </component>
</template>

<script setup>
import { computed } from "vue";

import {
  SHOW_ALIASES,
  SHOW_VALUES,
  STATUS_ALIASES,
  STATUS_LABELS,
  TOOLTIPS,
  VALID_STATUSES,
} from "./changePayCardSmallConstants.js";

import ChangePayCardSmallButtons from "@components/ChangePayCardSmallButtons/ChangePayCardSmallButtons.vue";
import EXTooltip from "@components/tooltip/EXTooltip.vue";
import NotAllowedReason from "@components/NotAllowedReason/NotAllowedReason.vue";
import MenuIcon from "@components/MenuIcon/MenuIcon.vue";
import { useFairPayMoreActionsFlag } from "@components/utils/useFairPayMoreActionsFlag";
import activityHeartIcon from "@components/ChangePayCardSmallButtons/icons/activity-heart.svg";
import settingsAccentIcon from "@components/ChangePayCardSmallButtons/icons/settings-03-accent.svg";
import settingsGlyphIcon from "@components/ChangePayCardSmallButtons/icons/settings-03-icon.svg";
import xCloseIcon from "@components/assets/x-close.svg";
import dotsVerticalIcon from "@components/assets/dots-vertical.svg";

const props = defineProps({
  show: {
    type: String,
    default: "",
    validator(value) {
      if (!value) return true;
      const normalized = SHOW_ALIASES[value] || value;
      return SHOW_VALUES.includes(normalized);
    },
  },
  status: {
    type: String,
    default: "",
    validator(value) {
      if (!value) return true;
      const normalized = STATUS_ALIASES[value] || value;
      return VALID_STATUSES.includes(normalized);
    },
  },
  date: {
    type: String,
    default: "",
  },
  amount: {
    type: String,
    default: "",
  },
  removable: {
    type: Boolean,
    default: true,
  },
  nextEvalDate: {
    type: String,
    default: "",
  },
  recordId: {
    type: [String, Number],
    default: null,
  },
  // Legacy props — kept for PayPlansTable and existing stories
  mode: {
    type: String,
    default: "",
    validator(value) {
      return !value || ["buttons", "proposal", "not-eligible", "upcoming"].includes(value);
    },
  },
  button: {
    type: String,
    default: "propose-changes",
    validator(value) {
      return [
        "evaluate-eligibility",
        "propose-changes",
        "propose-first-pay-plan",
        "propose-pay-plan",
      ].includes(value);
    },
  },
  buttons: {
    type: Array,
    default: null,
  },
  // Overrides the Propose Pay Plan button copy (e.g. "Add Pay Plan Proposal").
  buttonLabel: {
    type: String,
    default: "",
  },
  effectiveDate: {
    type: String,
    default: "",
  },
  summaryLine: {
    type: String,
    default: "",
  },
  amountFormatted: {
    type: String,
    default: "",
  },
  basisLabel: {
    type: String,
    default: "",
  },
  upcomingLabel: {
    type: String,
    default: "",
  },
  statusRail: {
    type: String,
    default: "",
  },
  showClose: {
    type: Boolean,
    default: true,
  },
  // Rule B-18 (ER-12109): while a future plan exists, the Propose Changes
  // button is disabled with an EXTooltip ("Edit, delete, or void the open
  // proposal first").
  proposeChangesBlocked: {
    type: Boolean,
    default: false,
  },
  // ER-12691: the API-reported block ({ title, description, versionId, ... }).
  // Replaces the blocked control with the "Not allowed" reason; click emits
  // `blocked-open` with this object.
  blocked: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits([
  "blocked-open",
  "remove",
  "evaluate-eligibility",
  "propose-changes",
  "propose-first-plan",
  "propose-pay-plan",
  "click",
  "void",
  // Legacy events
  "close-click",
  "button-click",
]);

const resolvedShow = computed(() => {
  if (props.show) return SHOW_ALIASES[props.show] || props.show;

  switch (props.mode) {
    case "proposal":
      return "Proposal";
    case "not-eligible":
      return "Evaluation";
    case "upcoming":
      return "FuturePlan";
    case "buttons":
    default:
      return "Buttons";
  }
});

const resolvedStatus = computed(() => {
  const rawStatus = props.status ? (STATUS_ALIASES[props.status] || props.status) : "";

  if (rawStatus) return rawStatus;

  if (resolvedShow.value === "Evaluation") {
    return "NotEligible";
  }

  if (resolvedShow.value === "FuturePlan") {
    return "N/A";
  }

  if (resolvedShow.value === "Proposal") {
    return "Draft";
  }

  switch (props.button) {
    case "propose-first-pay-plan":
      return "ProposeFirstPlan";
    case "propose-pay-plan":
      return "ProposePayPlan";
    case "evaluate-eligibility":
    case "propose-changes":
    default:
      return "ProposeChanges";
  }
});

const displayDate = computed(() => props.date || props.effectiveDate);

const displayAmount = computed(() => {
  if (props.amount) return props.amount;
  if (resolvedShow.value === "Proposal") return props.summaryLine || "Draft proposal";
  return props.amountFormatted;
});

const displayBasis = computed(() => props.basisLabel || "USD/year");

const futurePlanLabel = computed(() => {
  if (props.date) return `Effective from ${props.date}`;
  return props.upcomingLabel;
});

const isRemovable = computed(() => props.removable && props.showClose);

const moreActionsEnabled = useFairPayMoreActionsFlag();

// ER-12693: Proposal / FuturePlan chips replace the bare × with a ⋮ More actions menu.
const showMoreActions = computed(
  () =>
    moreActionsEnabled.value &&
    (resolvedShow.value === "Proposal" || resolvedShow.value === "FuturePlan")
);

const menuItems = computed(() => {
  const isPlan = resolvedShow.value === "FuturePlan";
  const items = [{ id: "open", name: isPlan ? "Open plan" : "Open proposal" }];
  if (props.removable) {
    items.push({ id: "void", name: isPlan ? "Void\u2026" : "Void proposal\u2026", destructive: true });
  }
  return items;
});

// ECL visual-states table + Figma 2706:12963 show close on Evaluation; Proposal/FuturePlan respect removable
// until the ⋮ menu (idea-MDP-429) takes over.
const showCloseButton = computed(() => {
  if (resolvedShow.value === "Evaluation") return true;
  if (resolvedShow.value === "Proposal" || resolvedShow.value === "FuturePlan") {
    return !moreActionsEnabled.value && isRemovable.value;
  }
  return false;
});

const statusRailColor = computed(() => {
  if (resolvedShow.value === "Proposal") {
    switch (resolvedStatus.value) {
      case "PendingApprovals":
        return "blue";
      case "Rejected":
        return "error";
      case "PendingReconciliation":
        return "success";
      case "Draft":
      default:
        return "neutral";
    }
  }

  if (resolvedShow.value === "Evaluation") {
    return resolvedStatus.value === "Eligible" ? "success" : "error";
  }

  return "none";
});

const resolvedButtons = computed(() => {
  if (Array.isArray(props.buttons) && props.buttons.length) {
    return props.buttons;
  }
  // Figma: Propose Changes + next-eval stacks Evaluate Eligibility above Propose Changes
  if (props.button === "propose-changes" && props.nextEvalDate) {
    return ["evaluate-eligibility", "propose-changes"];
  }
  return [props.button];
});

const showStatusRail = computed(
  () =>
    resolvedShow.value === "Proposal" ||
    resolvedShow.value === "Evaluation"
);

const rootClasses = computed(() => [
  `change-pay-card-small--${resolvedShow.value.toLowerCase().replace(/\s+/g, "-")}`,
  resolvedShow.value === "Buttons"
    ? `change-pay-card-small--${legacyButtonClass.value}`
    : "",
  resolvedShow.value === "Evaluation"
    ? `change-pay-card-small--${resolvedStatus.value === "Eligible" ? "eligible" : "not-eligible"}`
    : "",
  {
    "change-pay-card-small--bordered":
      resolvedShow.value === "Proposal" || resolvedShow.value === "Evaluation",
    "change-pay-card-small--buttons-only": resolvedShow.value === "Buttons",
  },
]);

const legacyButtonClass = computed(() => {
  switch (resolvedStatus.value) {
    case "ProposeFirstPlan":
      return "propose-first-pay-plan";
    case "ProposePayPlan":
      return "propose-pay-plan";
    default:
      return "propose-changes";
  }
});

const tooltipContent = computed(() => {
  if (resolvedShow.value === "Buttons") {
    return null;
  }

  if (resolvedShow.value === "FuturePlan") {
    const config = TOOLTIPS.FuturePlan;
    return {
      title: config.title,
      description: config.action,
    };
  }

  const config = TOOLTIPS[resolvedShow.value];
  if (!config) return null;

  const statusLabel = STATUS_LABELS[resolvedStatus.value];
  const description = statusLabel
    ? `${config.statusPrefix}${statusLabel}\n${config.action}`
    : config.action;

  return {
    title: config.title,
    description,
  };
});

const tooltipWrapper = computed(() => (tooltipContent.value ? EXTooltip : "div"));

const tooltipProps = computed(() => {
  if (!tooltipContent.value) return {};
  return {
    placement: "top",
    title: tooltipContent.value.title,
    description: tooltipContent.value.description,
    delay: 200,
  };
});

function handleRemove(event) {
  emit("remove", props.recordId);
  emit("close-click", event);
}

function handleCardClick(event) {
  if (event.target.closest(".change-pay-card-small__close, .change-pay-card-small__more")) {
    return;
  }
  emit("click", props.recordId);
}

function handleMenuAction(item) {
  if (item?.id === "open") emit("click", props.recordId);
  else if (item?.id === "void") emit("void", props.recordId);
}

function handleEvaluateEligibility(event) {
  emit("evaluate-eligibility", props.recordId);
  emit("button-click", event);
}

function handleProposeChanges(event) {
  emit("propose-changes", props.recordId);
  emit("button-click", event);
}

function handleProposeFirstPlan(event) {
  emit("propose-first-plan", props.recordId);
  emit("button-click", event);
}

function handleProposePayPlan(event) {
  emit("propose-pay-plan", props.recordId);
  emit("button-click", event);
}
</script>

<style scoped>
.change-pay-card-small-tooltip {
  display: inline-flex;
  width: fit-content;
  max-width: 100%;
}

.change-pay-card-small {
  display: inline-flex;
  align-items: stretch;
  width: fit-content;
  max-width: 100%;
  overflow: hidden;
  border-radius: 8px;
  background: #fff;
  vertical-align: middle;
}

.change-pay-card-small--bordered {
  border: 1px solid #eaecf0;
}

.change-pay-card-small--proposal,
.change-pay-card-small--evaluation,
.change-pay-card-small--not-eligible,
.change-pay-card-small--eligible {
  width: 165px;
}

.change-pay-card-small--buttons-only {
  align-items: center;
  min-height: 58px;
  width: 165px;
  background: transparent;
}

.change-pay-card-small--buttons-only.change-pay-card-small--propose-first-pay-plan,
.change-pay-card-small--buttons-only.change-pay-card-small--propose-pay-plan {
  width: 176px;
}

.change-pay-card-small--futureplan {
  align-items: center;
  width: 185px;
  background: transparent;
}

.change-pay-card-small__rail {
  flex-shrink: 0;
  width: 4px;
}

.change-pay-card-small__rail--neutral {
  background: #98a2b3;
}

.change-pay-card-small__rail--blue {
  background: #1570ef;
}

.change-pay-card-small__rail--error {
  background: #d92d20;
}

.change-pay-card-small__rail--success {
  background: #079455;
}

.change-pay-card-small__rail--none {
  display: none;
}

.change-pay-card-small__buttons-body {
  display: flex;
  flex: 1 0 0;
  flex-direction: column;
  gap: 2px;
  justify-content: center;
  min-width: 0;
  padding: 4px;
}

.change-pay-card-small__next-eval {
  margin: 0;
  width: 135px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  color: #344054;
}

.change-pay-card-small__body {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 5px;
  min-width: 0;
  padding: 4px 7px;
  cursor: pointer;
}

.change-pay-card-small__header-row {
  display: flex;
  align-items: center;
  gap: 7px;
  width: 100%;
  min-width: 0;
}

.change-pay-card-small__header-icon {
  display: inline-flex;
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  align-items: center;
  justify-content: center;
}

.change-pay-card-small__header-icon-img {
  display: block;
  width: 24px;
  height: 24px;
}

.change-pay-card-small__settings-icon {
  position: relative;
  width: 24px;
  height: 24px;
  overflow: hidden;
}

.change-pay-card-small__settings-accent,
.change-pay-card-small__settings-glyph {
  position: absolute;
  display: block;
}

.change-pay-card-small__settings-accent {
  top: 16.67%;
  right: 12.5%;
  bottom: 16.67%;
  left: 12.5%;
}

.change-pay-card-small__settings-accent img,
.change-pay-card-small__settings-glyph img {
  position: absolute;
  inset: -6.25% -5.56%;
  display: block;
  width: 100%;
  height: 100%;
  max-width: none;
}

.change-pay-card-small__settings-glyph {
  inset: 12.5%;
}

.change-pay-card-small__date {
  flex: 1 0 0;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  color: #344054;
}

.change-pay-card-small__summary-row {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  min-width: 0;
}

.change-pay-card-small__summary {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: pre;
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  color: #344054;
}

.change-pay-card-small__badge-row {
  display: flex;
  align-items: center;
  width: 100%;
}

.change-pay-card-small__badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex: 1 0 0;
  min-width: 0;
  height: 22px;
  padding: 2px 10px;
  border-radius: 9999px;
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: normal;
  white-space: nowrap;
}

.change-pay-card-small__badge--not-eligible {
  border: 1px solid #fecdca;
  background: #fef3f2;
  color: #b42318;
}

.change-pay-card-small__badge--eligible {
  border: 1px solid #abefc6;
  background: #ecfdf3;
  color: #067647;
}

.change-pay-card-small__badge-dot {
  flex-shrink: 0;
  width: 6px;
  height: 6px;
  border-radius: 50%;
}

.change-pay-card-small__badge--not-eligible .change-pay-card-small__badge-dot {
  background: #b42318;
}

.change-pay-card-small__badge--eligible .change-pay-card-small__badge-dot {
  background: #067647;
}

.change-pay-card-small__future-plan {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 6px;
  justify-content: center;
  min-width: 0;
  padding: 4px 4px 6px 7px;
  border: 1px solid #36bffa;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 1px 1px rgba(16, 24, 40, 0.05);
  cursor: pointer;
}

.change-pay-card-small__future-plan-top {
  display: flex;
  align-items: center;
  gap: 7px;
  width: 100%;
  min-width: 0;
}

.change-pay-card-small__amount,
.change-pay-card-small__basis {
  flex-shrink: 0;
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  color: #344054;
  white-space: nowrap;
}

.change-pay-card-small__spacer {
  flex: 1 0 0;
  min-width: 0;
}

.change-pay-card-small__future-plan-label {
  margin: 0;
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  color: #344054;
}

.change-pay-card-small__close {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: 2px;
  border: none;
  border-radius: 4px;
  background: transparent;
  cursor: pointer;
}

.change-pay-card-small__more {
  display: flex;
  flex-shrink: 0;
}

.change-pay-card-small__more-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
}

.change-pay-card-small__more-button:hover {
  background: #f2edff;
}

.change-pay-card-small__more-button:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: 1px;
}

.change-pay-card-small__close img {
  display: block;
  width: 12px;
  height: 12px;
}
</style>
